import { Grid } from '@mui/material';
import { CustomTabPanel } from '../CustomPanel';
import { ThemePalette } from 'mui.theme';
import FleetProductionDetails from './components/FleetProductionDetails';
import FleetProductionChart from './components/FleetProductionChart';
import AverageCycleTime from './components/AverageCycleTime';
import Suggestions from '../components/Suggestions/Suggestions';
import AnalyticsWrapper from './components/AnalyticsWrapper';
import AssetWrapper from './components/AssetWrapper';
import DateRangeDiv from '../HeaderMenu/DateRangeDiv';
import type {
  fleetData,
  productionAsset,
  productionLoadingStates,
} from 'types/production.types';
import {
  getHaulerSummary,
  getLoaderSummary,
  getProductionAssetsByTypeMapping,
} from './utils';
import LoaderAssetWrapper from './components/LoaderAssetWrapper';
import { useAppSelector } from 'store/hook';
import LoaderEventsDuration from '../Loader/component/LoaderEventsDuration';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  value: number;
  enableDarkTheme: boolean;
  isSelected: string;
  setIsSelected: any;
  handleDateChange: any;
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  yesterday: any;
  data: fleetData;
  loadingStates: productionLoadingStates;
  unitSystem: string;
  customerCode: string;
  assetOverview: any;
  aggregation: string;
  setAggregation: any;
  isPrinting: boolean;
  setDetailsVal: any;
  setAnalyticsData: any;
  setHoursBreakDown: any;
  setUserSelections: any;
  setSuggestionData: any;
  selectedChartSource: any;
  setSelectedChartSource: any;
  rollingDailyAverage: number;
  currentDailyAverage: number;
  shiftWiseAnalysis: boolean;
  setShiftWiseAnalysis: any;
  shiftNumber: number;
  setShiftNumber: any;
  handleFetchCustomDateRangeData: ({
    aggregation,
  }: {
    aggregation: string;
  }) => void;
  handleFetchCustomOnAggregationChange: ({
    aggregation,
    startDate,
    endDate,
  }: {
    aggregation: string;
    startDate: Date;
    endDate: Date;
  }) => void;
}

const AllFleet = ({
  value,
  enableDarkTheme,
  isSelected,
  setIsSelected,
  handleDateChange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  yesterday,
  data,
  loadingStates,
  unitSystem,
  customerCode,
  assetOverview,
  aggregation,
  setAggregation,
  isPrinting,
  setDetailsVal,
  setAnalyticsData,
  setHoursBreakDown,
  setUserSelections,
  setSuggestionData,
  selectedChartSource,
  setSelectedChartSource,
  rollingDailyAverage,
  currentDailyAverage,
  shiftWiseAnalysis,
  setShiftWiseAnalysis,
  shiftNumber,
  setShiftNumber,
  handleFetchCustomDateRangeData,
  handleFetchCustomOnAggregationChange,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const {
    data: {
      loaders: { allSummary },
    },
  } = useAppSelector((state) => state.productionReducer);

  const haulersSummary: any = getHaulerSummary(data.summary, t);
  const loaderSummary: any = getLoaderSummary(allSummary ?? [], t);

  const haulerAssets: productionAsset[] = getProductionAssetsByTypeMapping(
    data.assets,
    'Haul Truck'
  );

  return (
    <>
      <CustomTabPanel value={value} index={0}>
        <div>
          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            sx={{
              color: palette.textPrimary,
            }}
          >
            <FleetProductionDetails
              enableDarkTheme={enableDarkTheme}
              data={data.totalFleetDetails}
              isLoading={loadingStates.isLoadingFleetDetails}
              unitSystem={unitSystem}
              currentDailyAverage={currentDailyAverage}
              rollingDailyAverage={rollingDailyAverage}
              isLoadingFleetPeriodSummary={
                loadingStates.isLoadingFleetPeriodSummary
              }
              setDetailsVal={setDetailsVal}
            />
            <FleetProductionChart
              enableDarkTheme={enableDarkTheme}
              data={data.fleetPeriodSummary}
              isLoading={loadingStates.isLoadingFleetPeriodSummary}
              unitSystem={unitSystem}
              title={t('daily_fleet_production')}
              // setRollingDailyAverage={setRollingDailyAverage}
              setUserSelections={setUserSelections}
              selectedChartSource={selectedChartSource}
              setSelectedChartSource={setSelectedChartSource}
            />
          </Grid>

          <DateRangeDiv
            enableDarkTheme={enableDarkTheme}
            isSelected={isSelected}
            setIsSelected={setIsSelected}
            handleDateChange={handleDateChange}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            yesterday={yesterday}
            ignoreYtd={value === 2}
            aggregation={aggregation}
            setAggregation={setAggregation}
            shiftWiseAnalysis={shiftWiseAnalysis}
            setShiftWiseAnalysis={setShiftWiseAnalysis}
            shiftNumber={shiftNumber}
            setShiftNumber={setShiftNumber}
            handleFetchCustomDateRangeData={handleFetchCustomDateRangeData}
            handleFetchCustomOnAggregationChange={
              handleFetchCustomOnAggregationChange
            }
          />

          <AnalyticsWrapper
            enableDarkTheme={enableDarkTheme}
            data={data.summary}
            isLoading={loadingStates.isLoadingFleetSummary}
            unitSystem={unitSystem}
            assetOverview={assetOverview}
            scoreData={data.score}
            chartData={data.summaryChart}
            aggregation={aggregation}
            isSelected={isSelected}
            customerCode={customerCode}
            setAnalyticsData={setAnalyticsData}
          />
          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            sx={{
              paddingTop: '15px',
              color: palette.textPrimary,
            }}
          >
            <Grid item lg={9} sm={12} md={12}>
              <Grid container spacing={2} justifyContent="space-between">
                <AverageCycleTime
                  enableDarkTheme={enableDarkTheme}
                  title={t('average_hauler_cycle_time')}
                  data={haulersSummary}
                  isLoading={loadingStates.isLoadingFleetSummary}
                  setHoursBreakDown={(value: any) =>
                    setHoursBreakDown((prev: any) => ({
                      ...prev,
                      hauler: value,
                    }))
                  }
                />
                <LoaderEventsDuration
                  enableDarkTheme={enableDarkTheme}
                  isLoading={loadingStates.isLoadingLoaders}
                />
              </Grid>
            </Grid>
            <Suggestions
              enableDarkTheme={enableDarkTheme}
              minHeight="300px"
              data={data.suggestions}
              isLoading={loadingStates.isLoadingSuggestions}
              startDate={startDate as string}
              isSelected={isSelected}
              setSuggestionData={setSuggestionData}
            />
          </Grid>

          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            sx={{
              paddingTop: '15px',
              color: palette.textPrimary,
            }}
          >
            <AssetWrapper
              enableDarkTheme={enableDarkTheme}
              // Updated to use standard translation key for haulers
              title={t('std_asset_haulers')}
              data={haulerAssets}
              isLoading={loadingStates.isLoadingFleetAssets}
              aggregation={aggregation}
            />
            <LoaderAssetWrapper
              enableDarkTheme={enableDarkTheme}
              // Updated to use standard translation key for loaders
              title={t('std_asset_loaders')}
              isLoading={loadingStates.isLoadingLoaders}
              unitSystem={unitSystem}
              customerCode={customerCode}
              isPrinting={isPrinting}
              aggregation={aggregation}
            />
          </Grid>
        </div>
      </CustomTabPanel>
    </>
  );
};

export default AllFleet;
